/**
 * External dependencies
 */
import { addQueryArgs }                 from '@wordpress/url';
import apiFetch                         from '@wordpress/api-fetch';
import { Fragment }                     from '@wordpress/element';
import interpolateComponents            from '@automattic/interpolate-components';
import React                            from 'react';

/**
 * Internal dependencies
 */
import { AutoCompleter }                from '@woocommerce/components/build-types/search/autocompleters';
import { computeSuggestionMatch }       from '@woocommerce/components/build/search/autocompleters/utils';
import { __ }                           from '@wordpress/i18n';

interface AttributeTerm {
    id: number;
    name: string;
    attribute_name: string;
    attribute_label: string;
    taxonomy: string;
}

const completer: AutoCompleter = {
    name: 'product_attribute_terms',
    className: 'woocommerce-search__product-result',
    async options( search ) {
        try {
            // Fetch all product attributes first
            const attributes: any[] = await apiFetch( {
                path: addQueryArgs( 'wc/v3/products/attributes', {} ),
            } );

            if ( !attributes || attributes.length === 0 ) {
                return [];
            }

            // Fetch terms for each attribute
            const termsPromises = attributes.map( async ( attribute ) => {
                const query = search
                    ? {
                        search,
                        per_page: 100,
                    }
                    : {
                        per_page: 100,
                    };

                try {
                    const terms: any[] = await apiFetch( {
                        path: addQueryArgs( `wc/v3/products/attributes/${attribute.id}/terms`, query ),
                    } );

                    // Add attribute information to each term
                    // Handle slug that may or may not already have pa_ prefix
                    const taxonomy = attribute.slug.startsWith( 'pa_' ) ? attribute.slug : `pa_${attribute.slug}`;

                    return terms.map( ( term ) => ( {
                        ...term,
                        attribute_name: attribute.name,
                        attribute_label: attribute.name,
                        taxonomy: taxonomy,
                    } ) );
                } catch ( error ) {
                    console.error( `Failed to fetch terms for attribute ${attribute.name}:`, error );
                    return [];
                }
            } );

            const allTermsArrays = await Promise.all( termsPromises );
            const allTerms: AttributeTerm[] = allTermsArrays.flat();

            // Filter by search if provided
            if ( search ) {
                return allTerms.filter( ( term ) =>
                    term.name.toLowerCase().includes( search.toLowerCase() ) ||
                    term.attribute_label.toLowerCase().includes( search.toLowerCase() ),
                );
            }

            return allTerms;
        } catch ( error ) {
            console.error( 'Failed to fetch product attributes:', error );
            return [];
        }
    },
    isDebounced: true,
    getOptionIdentifier( term: AttributeTerm ) {
        return term.id;
    },
    getOptionKeywords( term: AttributeTerm ) {
        return [ term.name, term.attribute_label ];
    },
    getFreeTextOptions( query ) {
        const label = (
            <span key="name" className="woocommerce-search__result-name">
                {interpolateComponents( {
                    mixedString: __(
                        'All attribute terms that include {{query /}}',
                        'checkout-wc',
                    ),
                    components: {
                        query: (
                            <strong className="components-form-token-field__suggestion-match">
                                {query}
                            </strong>
                        ),
                    },
                } )}
            </span>
        );
        const titleOption = {
            key: 'title',
            label,
            value: { id: query, name: query, attribute_label: '', taxonomy: '' },
        };

        return [ titleOption ];
    },
    getOptionLabel( term: AttributeTerm, query ) {
        const displayText = `${term.attribute_label}: ${term.name}`;
        const match = computeSuggestionMatch( displayText, query );
        return (
            <Fragment>
                <span
                    key="name"
                    className="woocommerce-search__result-name"
                    aria-label={displayText}
                >
                    {match?.suggestionBeforeMatch}
                    <strong className="components-form-token-field__suggestion-match">
                        {match?.suggestionMatch}
                    </strong>
                    {match?.suggestionAfterMatch}
                </span>
            </Fragment>
        );
    },
    // This is slightly different than Gutenberg's Autocomplete; we don't support different methods
    // of replace/insertion, so we can just return the value.
    getOptionCompletion( term: AttributeTerm ) {
        return {
            key: term.id,
            label: `${term.attribute_label}: ${term.name}`,
        };
    },
};

export default completer;
